import React, { useState, useMemo } from "react";
import { Row, Col, Collapse, Table, Select, Switch, Tooltip, Tag, Button } from "antd";
import { useFetch } from "../../hooks/useFetch";
import { useStore } from "../../hooks/useStore";
import { Link } from "react-router-dom";
import { CompanyFilter, SubCompanyFilter } from "../../components/_defaultComponents/FilterComponents";
import { FormHeader } from "../../components/shiftRequest/ShiftRequest.styles";
import { SearchInput } from "../../components/_defaultComponents/FilterComponents/FilterComponentStyles";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { codPaymentTypes } from "../../constants/constants";
import { DocumentModal } from "../../components/documents/documentModal";

export const SubcompanyCodPaymentListPage = () => {
    const stores = useStore();
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [companyId, setCompanyId] = useState(undefined);
    const [subCompanyId, setSubCompanyId] = useState(undefined);
    const [vigoPosStatus, setVigoPosStatus] = useState("true");
    const [paymentMethods, setPaymentMethods] = useState("all");
    const [paymentMethodSelection, setPaymentMethodSelection] = useState("all");
    const [paymentMethodSelectionStatus, setPaymentMethodSelectionStatus] = useState("all");
    const [contractStatus, setContractStatus] = useState("all");
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);
    const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
    const [selectedDocumentUrl, setSelectedDocumentUrl] = useState(null);

    const getTagColor = (method) => {
        const colorMap = {
            creditCard: "green",
            cash: "blue",
            mealCard: "orange",
        };
        return colorMap[method] || "default";
    };

    const handleDocumentClick = (record) => {
        if (record.uploadedDocumentPath) {
            setSelectedDocumentUrl(record.uploadedDocumentPath);
            setIsDocumentModalVisible(true);
        } else if (record.contractPath) {
            setSelectedDocumentUrl(record.contractPath);
            setIsDocumentModalVisible(true);
        }
    };

    const handleOnCancelDocumentModal = () => {
        setIsDocumentModalVisible(false);
        setSelectedDocumentUrl(null);
    };

    const [subcompanyListLoading, subcompanyList, _, __, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubcompanyCodPaymentList.bind(null, {
            companyId,
            subCompanyId,
            limit: 10,
            page: currentPage,
            vigoPosStatus: vigoPosStatus === "all" ? undefined : vigoPosStatus === "true",
            paymentMethods: paymentMethods !== "all" ? paymentMethods : undefined,
            paymentMethodSelection: paymentMethodSelection === "all" ? undefined : paymentMethodSelection === "true",
            paymentMethodSelectionStatus: paymentMethodSelectionStatus === "all" ? undefined : paymentMethodSelectionStatus === "true",
            contractStatus: contractStatus !== "all" ? contractStatus === "true" : undefined,
            searchText: searchText && searchText.toLowerCase()
        }),
        showLoading: true,
        dependencies: [
            companyId,
            subCompanyId,
            currentPage,
            vigoPosStatus,
            paymentMethods,
            paymentMethodSelection,
            paymentMethodSelectionStatus,
            contractStatus,
            searchText
        ]
    });

    const columns = [
        {
            title: "Firma",
            dataIndex: "companyName",
            key: "companyName",
            render: (text, record) => (
                <Link to={`/companies/${record.companyId}`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: "Alt Firma",
            dataIndex: "subCompanyName",
            key: "subCompanyName",
            render: (text, record) => (
                <Link to={`/companies/sub-company/${record.subCompanyId}?tab=vigoPos`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: (
                <>
                    VigoPOS Durumu{" "}
                    <Tooltip title="VigoPOS terminal hakkı durumu">
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: "vigoPosStatus",
            key: "vigoPosStatus",
            render: (status) => status ? "Aktif" : "Pasif"
        },
        {
            title: "Ödeme Yöntemleri",
            dataIndex: "paymentMethods",
            key: "paymentMethods",
            render: (methods) => methods?.length ? (
                <>
                    {methods.map((method, index) => {
                        const paymentType = codPaymentTypes.find(type => type.value === method);
                        return (
                            <Tag key={index} color={getTagColor(method)}>
                                {paymentType?.label || method}
                            </Tag>
                        );
                    })}
                </>
            ) : "-"
        },
        {
            title: "Ödeme Yöntemi Seçim Ayarı",
            dataIndex: "paymentMethodSelection",
            key: "paymentMethodSelection",
            render: (selection) => selection ? "Aktif" : "Pasif"
        },
        {
            title: "Ödeme Yöntemi Seçim Durumu",
            dataIndex: "paymentMethodSelectionStatus",
            key: "paymentMethodSelectionStatus",
            render: (status) => status ? "Seçim Yapmış" : "Henüz Seçim Yapmamış"
        },
        {
            title: (
                <>
                    İmzalı Fiziksel Sözleşme{" "}
                    <Tooltip title={
                        <div>
                            <p>Sözleşme Durumları:</p>
                            <p>1. Müşteriden Onay Bekleyen Sözleşme</p>
                            <p>2. Müşteri Onaylı Dijital Sözleşme</p>
                            <p>3. Fiziksel İmzalı Sözleşme</p>
                            <p style={{ marginTop: '8px', fontSize: '12px' }}>→ Süreç bu sırayla ilerler</p>
                        </div>
                    }>
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: "contractStatus",
            key: "contractStatus",
            render: (status, record) => {
                if (record.uploadedDocumentPath) {
                    return (
                        <a onClick={() => handleDocumentClick(record)}>
                            Fiziksel İmzalı Sözleşme
                        </a>
                    );
                } else if (record.contractPath) {
                    return (
                        <a onClick={() => handleDocumentClick(record)}>
                            {record.isContractApproved ? "Müşteri Onaylı Dijital Sözleşme" : "Müşteriden Onay Bekleyen Sözleşme"}
                        </a>
                    );
                }
                return status ? "Var" : "Yok";
            }
        }
    ];

    return (
        <div style={{ display: "flex", flexDirection: "column", overflow: "scroll" }}>
            <FormHeader>
                Kapıda Ödeme Alt Firmalar
            </FormHeader>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Row>
                    <Col span={24}>
                        <Collapse
                            activeKey={isFiltersVisible ? "1" : null}
                            onChange={() => setIsFiltersVisible(!isFiltersVisible)}
                            style={{ marginTop: "25px", marginBottom: "25px" }}
                        >
                            <Collapse.Panel header="Filtreler" key="1">
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <CompanyFilter
                                            value={companyId}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setCompanyId(value);
                                            }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <SubCompanyFilter
                                            value={subCompanyId}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setSubCompanyId(value);
                                            }}
                                            company={companyId}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>VigoPOS Durumu</h5>
                                        <Select
                                            style={{ width: "100%" }}
                                            value={vigoPosStatus}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setVigoPosStatus(value);
                                            }}
                                        >
                                            <Select.Option value="all">Tümü</Select.Option>
                                            <Select.Option value="true">Aktif</Select.Option>
                                            <Select.Option value="false">Pasif</Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Ödeme Yöntemleri</h5>
                                        <Select
                                            style={{ width: "100%" }}
                                            value={paymentMethods}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setPaymentMethods(value);
                                            }}
                                        >
                                            <Select.Option value="all">Tümü</Select.Option>
                                            <Select.Option value="creditCard">Kredi Kartı</Select.Option>
                                            <Select.Option value="mealCard">Yemek Kartı</Select.Option>
                                            <Select.Option value="cash">Nakit</Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Ödeme Yöntemi Seçim Ayarı</h5>
                                        <Select
                                            style={{ width: "100%" }}
                                            value={paymentMethodSelection}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setPaymentMethodSelection(value);
                                            }}
                                        >
                                            <Select.Option value="all">Tümü</Select.Option>
                                            <Select.Option value="true">Aktif</Select.Option>
                                            <Select.Option value="false">Pasif</Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Ödeme Yöntemi Seçim Durumu</h5>
                                        <Select
                                            style={{ width: "100%" }}
                                            value={paymentMethodSelectionStatus}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setPaymentMethodSelectionStatus(value);
                                            }}
                                        >
                                            <Select.Option value="all">Tümü</Select.Option>
                                            <Select.Option value="true">Seçim Yapmış</Select.Option>
                                            <Select.Option value="false">Henüz Seçim Yapmamış</Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>İmzalı Fiziksel Sözleşme</h5>
                                        <Select
                                            style={{ width: "100%" }}
                                            value={contractStatus}
                                            onChange={(value) => {
                                                setCurrentPage(1);
                                                setContractStatus(value);
                                            }}
                                        >
                                            <Select.Option value="all">Tümü</Select.Option>
                                            <Select.Option value="true">Var</Select.Option>
                                            <Select.Option value="false">Yok</Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Arama</h5>
                                        <SearchInput
                                            placeholder="Ara..."
                                            value={searchText}
                                            onSearch={(value) => {
                                                setCurrentPage(1);
                                                setSearchText(value);
                                            }}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </Col>
                                </Row>
                            </Collapse.Panel>
                        </Collapse>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={subcompanyList?.data?.rows}
                    loading={subcompanyListLoading}
                    rowKey="_id"
                    pagination={{
                        current: currentPage,
                        pageSize: 10,
                        total: subcompanyList?.data?.dataCount,
                        onChange: (page) => setCurrentPage(page)
                    }}
                />

                <DocumentModal
                    visibility={isDocumentModalVisible && selectedDocumentUrl !== null}
                    onCancel={handleOnCancelDocumentModal}
                    documentUrl={selectedDocumentUrl}
                />
            </div>
        </div>
    );
}; 